import { apm } from '@elastic/apm-rum';
import { UserManager } from 'oidc-client';
import { configuration } from '../configuration';
import { session } from '../utilities/session-storage';
import { win } from '../utilities/window';

// written by public/logged-out-elsewhere.js
export const loggedOutAtKey = 'fasit-logged-out-at';
export const signedInAtKey = 'fasit-signed-in-at';

function rememberSignIn() {
  session.setItem(signedInAtKey, String(Date.now()));
}

function loggedOutAt(): number {
  try {
    return Number(win.localStorage.getItem(loggedOutAtKey)) || 0;
  } catch {
    return 0;
  }
}

// both times are this browser's clock, so the STS's clock cannot make a fresh login look older than the logout
export function isLoggedOutSinceSignIn(): boolean {
  const logout = loggedOutAt();
  return logout > 0 && Number(session.getItem(signedInAtKey) ?? 0) < logout;
}

export function showLoggedOut() {
  win.location.assign(`${configuration.baseAddress}/logged-out`);
}

/** What a tab's login lets the logout mark see and end. */
interface TabSession {
  rememberSignIns(rememberSignIn: () => void): void;
  isSignedIn(): Promise<boolean>;
  end(): Promise<void>;
}

async function endSessionIfLoggedOutElsewhere(tabSession: TabSession) {
  if (!isLoggedOutSinceSignIn() || !(await tabSession.isSignedIn())) {
    return;
  }

  await tabSession.end();
  showLoggedOut();
}

/**
 * A logout in another tab, or in another app of the fasit session, leaves this tab's user signed in, so the tab ends it
 * when it sees the mark: at once if it is awake, otherwise when it is shown or started again.
 */
function endTabSessionWhenLoggedOutElsewhere(tabSession: TabSession) {
  function check() {
    endSessionIfLoggedOutElsewhere(tabSession).catch((error: unknown) => apm.captureError(error as Error));
  }

  tabSession.rememberSignIns(rememberSignIn);
  win.addEventListener('storage', event => {
    if (event.key === loggedOutAtKey) {
      check();
    }
  });
  win.document.addEventListener('visibilitychange', () => {
    if (win.document.visibilityState === 'visible') {
      check();
    }
  });
  check();
}

export function endSessionWhenLoggedOutElsewhere(userManager: UserManager) {
  endTabSessionWhenLoggedOutElsewhere({
    rememberSignIns: remember => userManager.events.addUserLoaded(remember),
    isSignedIn: async () => Boolean(await userManager.getUser()),
    end: () => userManager.removeUser(),
  });
}

/** The BFF has already ended the cookie session when the mark is written, so the tab only has to show it. */
export function endCookieSessionWhenLoggedOutElsewhere() {
  endTabSessionWhenLoggedOutElsewhere({
    rememberSignIns: remember => remember(),
    isSignedIn: () => Promise.resolve(true),
    end: () => Promise.resolve(),
  });
}
